import { useMutation, UseMutationResult } from "@tanstack/react-query";
import {
  login,
  requestOtp,
  registerUser,
  refreshToken,
  requestOtpForLogin,
  verifyOtpAndLogin,
  resendOtp,
  logout,
  requestOtpForPasswordReset,
  resetPasswordWithOtp,
} from "@/services/auth";
import type {
  LoginRequest,
  RequestOtpPayload,
  RegisterRequest,
  RefreshTokenPayload,
  RequestOtpLoginPayload,
  VerifyOtpPayload,
  ResendOtpPayload,
  RequestOtpPasswordResetPayload,
  ResetPasswordWithOtpPayload,
  VerifyOtpSuccess,
  VerifyOtpError,
  LoginErrorResponse,
  LoginSuccessResponse,
} from "@/types/auth";

export const useLogin = (): UseMutationResult<
  LoginSuccessResponse,
  LoginErrorResponse,
  LoginRequest
> => {
  return useMutation({
    mutationFn: (payload: LoginRequest) => login(payload),
    retry: false,
    onError: (error) => {
      console.log("OTP verification error:", error);
      if (typeof window !== "undefined") {
        localStorage.setItem("last_verify_error", JSON.stringify(error));

        const errorMessage =
          error?.error?.messages?.[0] || error?.reason || "خطای ناشناخته";
        localStorage.setItem("last_verify_error_message", errorMessage);

        if (
          error?.data?.active_sessions &&
          error.data.active_sessions.length > 0
        ) {
          localStorage.setItem(
            "active_sessions",
            JSON.stringify(error.data.active_sessions),
          );
        }

        localStorage.setItem("last_verify_error_time", Date.now().toString());
      }
    },
  });
};

export function useRequestOtp() {
  return useMutation({
    mutationFn: (payload: RequestOtpPayload) => requestOtp(payload),
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (payload: RegisterRequest) => registerUser(payload),
  });
}

export function useRefreshToken() {
  return useMutation({
    mutationFn: (payload: RefreshTokenPayload) => refreshToken(payload),
  });
}

export function useRequestOtpForLogin() {
  return useMutation({
    mutationFn: (payload: RequestOtpLoginPayload) =>
      requestOtpForLogin(payload),
  });
}

export const useVerifyOtp = (): UseMutationResult<
  VerifyOtpSuccess,
  VerifyOtpError,
  VerifyOtpPayload
> => {
  return useMutation({
    mutationFn: verifyOtpAndLogin,
    retry: false,
    onError: (error) => {
      if (typeof window !== "undefined") {
        localStorage.setItem("last_verify_error", JSON.stringify(error));

        const errorMessage =
          error?.error?.messages?.[0] || error?.reason || "خطای ناشناخته";
        localStorage.setItem("last_verify_error_message", errorMessage);

        if (
          error?.data?.active_sessions &&
          error.data.active_sessions.length > 0
        ) {
          localStorage.setItem(
            "active_sessions",
            JSON.stringify(error.data.active_sessions),
          );
        }

        localStorage.setItem("last_verify_error_time", Date.now().toString());
      }
    },
  });
};

export function useResendOtp() {
  return useMutation({
    mutationFn: (payload: ResendOtpPayload) => resendOtp(payload),
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => logout(),
  });
}

export function useRequestOtpForPasswordReset() {
  return useMutation({
    mutationFn: (payload: RequestOtpPasswordResetPayload) =>
      requestOtpForPasswordReset(payload),
  });
}

export function useResetPasswordWithOtp() {
  return useMutation({
    mutationFn: (payload: ResetPasswordWithOtpPayload) =>
      resetPasswordWithOtp(payload),
  });
}
